import React, { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, themeAlpine, AllCommunityModule, ModuleRegistry, RowClassParams, CellStyle } from 'ag-grid-community';
import { SurfaceTreatment } from '../types/CommonTypes';
import { useAssignment } from '../hooks/useAssignment';

type SurfaceTreatmentRow = SurfaceTreatment & { approved: 'yes' | 'no' };

interface Props {
    devMode: string | null;
    setSelectedTreatment: (r: SurfaceTreatment | undefined) => void;
    treatments?: SurfaceTreatment[];
    validSTs?: SurfaceTreatment[];
}

const APPROVED_CELL_STYLE: CellStyle = { color: '#166534', fontWeight: 600 };
const UNAPPROVED_CELL_STYLE: CellStyle = { color: '#9ca3af' };

export default function SurfaceTreatmentComponent({ devMode, setSelectedTreatment, treatments, validSTs }: Props): JSX.Element {
    ModuleRegistry.registerModules([AllCommunityModule]);
    const { assignSurfaceTreatment, assignedMaterial } = useAssignment();

    const validIdSet = useMemo<Set<string>>(
        () => new Set((validSTs ?? []).map(st => st.stId)),
        [validSTs]
    );

    const rows = useMemo<SurfaceTreatmentRow[]>(() => {
        const noMaterial = !assignedMaterial?.id;
        const augmented: SurfaceTreatmentRow[] = (treatments ?? []).map(st => ({
            ...st,
            approved: (noMaterial || validIdSet.has(st.stId)) ? 'yes' : 'no',
        }));
        augmented.sort((a, b) => {
            if (a.approved === b.approved) return 0;
            return a.approved === 'yes' ? -1 : 1;
        });
        return augmented;
    }, [treatments, validIdSet, assignedMaterial]);

    const columnDefs = useMemo<ColDef<SurfaceTreatmentRow>[]>(() => [
        { headerName: 'ID', field: 'stId', flex: 20, editable: devMode === 'psdev' },
        {
            headerName: 'Approved',
            field: 'approved',
            flex: 40,
            filter: 'agTextColumnFilter',
            filterParams: {
                maxNumConditions: 1,
                filterOptions: [
                    { displayKey: 'all', displayName: 'All', predicate: () => true, numberOfInputs: 0 },
                    { displayKey: 'approved', displayName: 'Approved', predicate: (_: string[], v: string) => v === 'yes' || !(assignedMaterial?.id), numberOfInputs: 0 },
                ],
                defaultOption: 'all',
            },
            cellStyle: params => params.value === 'yes' ? APPROVED_CELL_STYLE : UNAPPROVED_CELL_STYLE,
        },
        { headerName: 'Surface Treatment', field: 'stName', flex: 100, editable: devMode === 'psdev', valueFormatter: params => {
            const adb = (params.data as SurfaceTreatmentRow | undefined)?.adb;
            return adb ? `${adb}_${params.value ?? ''}` : (params.value ?? '');
        }},
        { headerName: 'Density', field: 'density', flex: 60, editable: devMode === 'psdev', valueFormatter: params => `${params.value} kg/dm³` },
        { headerName: 'Thickness', field: 'thickness', flex: 60, editable: devMode === 'psdev', valueFormatter: params => `${params.value} µm` },
    ], [devMode, assignedMaterial]);

    const getRowClass = (params: RowClassParams<SurfaceTreatmentRow>) =>
        params.data?.approved === 'yes' ? 'row-approved' : undefined;

    return (
        <div style={{ width: '100%', maxHeight: '75vh', minHeight: '15em' }}>
            <style>{`
                .row-approved {
                    background-color: #f0fdf4 !important;
                    box-shadow: inset 3px 0 0 #16a34a;
                }
                .row-approved:hover {
                    background-color: #dcfce7 !important;
                }
            `}</style>
            <AgGridReact<SurfaceTreatmentRow>
                rowData={rows}
                columnDefs={columnDefs}
                domLayout='autoHeight'
                theme={themeAlpine}
                onGridReady={e => e.api.setFilterModel({ approved: { filterType: 'text', type: 'approved' } })}
                rowSelection={{
                    mode: 'singleRow',
                    enableClickSelection: true,
                    checkboxes: false
                }}
                getRowClass={getRowClass}
                onSelectionChanged={e => {
                    const selectedRows = e.api.getSelectedRows();
                    setSelectedTreatment((selectedRows?.length || 0) > 0 ? selectedRows[0] : undefined);
                }}
                onRowDoubleClicked={e => {
                    if (e.data) {
                        setSelectedTreatment(e.data);
                        assignSurfaceTreatment();
                    }
                }}
            />
        </div>
    );
}
